// src/components/quality/DynamicsOperatorTable.js

import {Fragment} from "react";

const DynamicsOperatorTable = ({ assessmentsByWeek, fieldOptions, periodType }) => {
  // Извлекаем периоды из assessmentsByWeek (ключи объекта - это метки периодов)
  const periods = Object.keys(assessmentsByWeek).map(label => ({
    label,
    data: assessmentsByWeek[label] || []
  }));
  
  // Сортируем периоды в обратном порядке (от новых к старым)
  const sortedPeriods = periods.reverse().slice(0, 6);
  
  // Формируем данные: оператор -> период -> массив оценок
  const data = {};
  
  // Инициализируем структуру данных для каждого оператора
  fieldOptions.forEach(operator => {
    const operatorFullName = `${operator.surname} ${operator.name}`;
    data[operatorFullName] = {};
    
    // Инициализируем каждый период для каждого оператора
    periods.forEach(period => {
      data[operatorFullName][period.label] = [];
    });
  });
  
  // Распределяем оценки по операторам и периодам
  periods.forEach(period => {
    const periodLabel = period.label;
    
    period.data.forEach(assessment => {
      const assessmentType = assessment.agent_name;
      if (!assessmentType) return;
      
      if (data[assessmentType]) {
        data[assessmentType][periodLabel].push(assessment);
      }
    });
  });

  return (
    <div className="table-wrapper">
      <h3>Статистика по операторам</h3>
      <table className="stats-table">
        <thead>
        <tr>
          <th rowSpan="2">Оператор</th>
          <th rowSpan="2">Динамика</th>
          {sortedPeriods.map((period, index) => (
              <Fragment key={index}>
                <th colSpan="2">{period.label}</th>
              </Fragment>
          ))}
        </tr>
        <tr>
          {sortedPeriods.map((period, index) => (
              <Fragment key={index}>
                <th>Количество ошибок</th>
                <th>Средний % качества</th>
              </Fragment>
          ))}
        </tr>
        </thead>
        <tbody>
          {Object.entries(data)
            .map(([operatorFullName, periodsData]) => {
              const operatorName = operatorFullName;
              
              // Считаем общее количество оценок оператора
              const totalAssessments = Object.values(periodsData).flat().length;
              
              // Считаем данные для первых двух периодов для расчета динамики
              const firstPeriodData = sortedPeriods.length > 0 ? periodsData[sortedPeriods[0].label] || [] : [];
              const secondPeriodData = sortedPeriods.length > 1 ? periodsData[sortedPeriods[1].label] || [] : [];
              
              // Считаем средний процент для первого периода
              const firstPeriodTotalScore = firstPeriodData.reduce((sum, assessment) => sum + (assessment.total_score || 0), 0);
              const firstPeriodAvgQuality = firstPeriodData.length > 0 ? (firstPeriodTotalScore / firstPeriodData.length) * 100 : 0;
              
              // Считаем средний процент для второго периода
              const secondPeriodTotalScore = secondPeriodData.reduce((sum, assessment) => sum + (assessment.total_score || 0), 0);
              const secondPeriodAvgQuality = secondPeriodData.length > 0 ? (secondPeriodTotalScore / secondPeriodData.length) * 100 : 0;
              
              // Рассчитываем разницу в среднем качестве
              let diffText = '';
              let diffColor = '';
              
              // Проверяем, что в обоих периодах есть данные
              if (firstPeriodData.length > 0 && secondPeriodData.length > 0) {
                const diffPercent = firstPeriodAvgQuality - secondPeriodAvgQuality;
                
                // Округляем до целого числа
                const absDiff = Math.round(Math.abs(diffPercent));
                
                // Если разница положительная или 0 (улучшение или стабильность) - зеленый
                if (diffPercent >= 0) {
                  diffText = diffPercent > 0 ? `▲${absDiff}%` : `▲0%`;
                  diffColor = 'green';
                } else {
                  // Ухудшение - красный
                  diffText = `▼${absDiff}%`;
                  diffColor = 'red';
                }
              }
              
              return {
                operatorFullName,
                operatorName,
                totalAssessments,
                firstPeriodData,
                secondPeriodData,
                diffText,
                diffColor
              };
            })
            // Сортируем по количеству оценок (по убыванию)
            .sort((a, b) => b.totalAssessments - a.totalAssessments)
            .map(operator => {
              const { operatorFullName, operatorName, firstPeriodData, secondPeriodData, diffText, diffColor } = operator;
              const periodsData = data[operatorFullName];
              
              return (
                <tr key={operatorFullName}>
                  <td>{operatorName}</td>
                  <td style={{ color: diffColor, fontWeight: 'bold' }}>{diffText}</td>
                  {sortedPeriods.map(period => {
                    const periodLabel = period.label;
                    const periodAssessments = periodsData[periodLabel] || [];
                    
                    // Считаем количество ошибок
                    const zeroCount = periodAssessments.reduce((sum, assessment) => sum + (assessment.zero_count || 0), 0);
                    
                    // Считаем средний процент
                    const totalScore = periodAssessments.reduce((sum, assessment) => sum + (assessment.total_score || 0), 0);
                    const avgQuality = periodAssessments.length > 0 ? (totalScore / periodAssessments.length) * 100 : 0;
                    
                    return [
                      <td key={`${operatorFullName}-mistakes-${periodLabel}`}>
                        {periodAssessments.length > 0 ? zeroCount : ''}
                      </td>,
                      <td 
                        key={`${operatorFullName}-quality-${periodLabel}`}
                        style={{
                          backgroundColor: (() => {
                            if (periodAssessments.length === 0) return 'transparent';
                            if (avgQuality >= 93) return '#C6EFCE'; // Зеленый
                            // if (avgQuality >= 85) return '#FFEB9C'; // Желтый
                            return '#FFC7CE'; // Красный
                          })(),
                          textAlign: 'center',
                          fontWeight: 'bold'
                        }}
                      >
                        {periodAssessments.length > 0 ? `${avgQuality.toFixed(1)}%` : ''}
                      </td>
                    ];
                  })}
                </tr>
              );
            })}
        </tbody>
        <tfoot>
          <tr>
            <td>Всего</td>
            {/* Рассчитываем динамику для общих данных */}
            {(() => {
              // Считаем общие данные для первых двух периодов
              const firstPeriodLabel = sortedPeriods.length > 0 ? sortedPeriods[0].label : '';
              const secondPeriodLabel = sortedPeriods.length > 1 ? sortedPeriods[1].label : '';
              
              // Собираем все оценки за первый период
              let firstPeriodAssessments = [];
              let secondPeriodAssessments = [];
              
              Object.values(data).forEach(operatorData => {
                if (firstPeriodLabel) {
                  firstPeriodAssessments = firstPeriodAssessments.concat(operatorData[firstPeriodLabel] || []);
                }
                if (secondPeriodLabel) {
                  secondPeriodAssessments = secondPeriodAssessments.concat(operatorData[secondPeriodLabel] || []);
                }
              });
              
              // Считаем средний процент для первого периода
              const firstPeriodTotalScore = firstPeriodAssessments.reduce((sum, assessment) => sum + (assessment.total_score || 0), 0);
              const firstPeriodAvgQuality = firstPeriodAssessments.length > 0 ? (firstPeriodTotalScore / firstPeriodAssessments.length) * 100 : 0;
              
              // Считаем средний процент для второго периода
              const secondPeriodTotalScore = secondPeriodAssessments.reduce((sum, assessment) => sum + (assessment.total_score || 0), 0);
              const secondPeriodAvgQuality = secondPeriodAssessments.length > 0 ? (secondPeriodTotalScore / secondPeriodAssessments.length) * 100 : 0;
              
              // Рассчитываем разницу в среднем качестве
              let diffText = '';
              let diffColor = '';
              
              // Проверяем, что в обоих периодах есть данные
              if (firstPeriodAssessments.length > 0 && secondPeriodAssessments.length > 0) {
                const diffPercent = firstPeriodAvgQuality - secondPeriodAvgQuality;
                
                // Округляем до целого числа
                const absDiff = Math.round(Math.abs(diffPercent));
                
                // Если разница положительная или 0 (улучшение или стабильность) - зеленый
                if (diffPercent >= 0) {
                  diffText = diffPercent > 0 ? `▲${absDiff}%` : `▲0%`;
                  diffColor = 'green';
                } else {
                  // Ухудшение - красный
                  diffText = `▼${absDiff}%`;
                  diffColor = 'red';
                }
              }
              
              return <td style={{ color: diffColor, fontWeight: 'bold' }}>{diffText}</td>;
            })()}
            {/* Выводим общие данные по периодам */}
            {sortedPeriods.map(period => {
              const periodLabel = period.label;
              
              // Собираем все оценки за период
              let periodAssessments = [];
              Object.values(data).forEach(operatorData => {
                periodAssessments = periodAssessments.concat(operatorData[periodLabel] || []);
              });
              
              // Считаем количество ошибок
              const zeroCount = periodAssessments.reduce((sum, assessment) => sum + (assessment.zero_count || 0), 0);
              
              // Считаем средний процент
              const totalScore = periodAssessments.reduce((sum, assessment) => sum + (assessment.total_score || 0), 0);
              const avgQuality = periodAssessments.length > 0 ? (totalScore / periodAssessments.length) * 100 : 0;
              
              return [
                <td key={`total-mistakes-${periodLabel}`}>
                  {periodAssessments.length > 0 ? zeroCount : ''}
                </td>,
                <td 
                  key={`total-quality-${periodLabel}`}
                  style={{
                    backgroundColor: (() => {
                      if (periodAssessments.length === 0) return 'transparent';
                      if (avgQuality > 98) return '#C6EFCE'; // Зеленый
                      if (avgQuality >= 85) return '#FFEB9C'; // Желтый
                      return '#FFC7CE'; // Красный
                    })(),
                    textAlign: 'center',
                    fontWeight: 'bold'
                  }}
                >
                  {periodAssessments.length > 0 ? `${avgQuality.toFixed(1)}%` : ''}
                </td>
              ];
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default DynamicsOperatorTable;
